import { createStyles, makeUiStyles, Theme } from '../../styles/styles';
import { fontText, StylesIndexInjections } from 'common-react';

const FOCUS_GAP = 2;
const FOCUS_OUTLINE_WIDTH = 2;
const SEGMENTED_RADIUS = 8;
const SEGMENTED_RADIUS_INNER = 8;
const SEGMENTED_PADDING_V = 6;
const SEGMENTED_PADDING_H = 12;

export interface TabButtonStyleProps {
  checked?: boolean;
  variant?: 'big' | 'small';
}

export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: 0,
    borderRadius: SEGMENTED_RADIUS,
    backgroundColor: theme.variables.isDarkTheme ? theme.color.greys[70] : theme.color.greys[10],
    border: 'none',

    '& $multiButtonItem': {
      '&:not(:first-child)': {
        marginLeft: 0,
      },
      '&:first-child $radioLabel': {
        borderTopLeftRadius: SEGMENTED_RADIUS_INNER,
        borderBottomLeftRadius: SEGMENTED_RADIUS_INNER,
      },
      '&:last-child $radioLabel': {
        borderTopRightRadius: SEGMENTED_RADIUS_INNER,
        borderBottomRightRadius: SEGMENTED_RADIUS_INNER,
      },
    },
  },

  radioInput: {
    position: 'fixed',
    left: -9999,
    width: 0,
    height: 0,
  },

  radioLabel: {
    cursor: 'pointer',
    display: 'block',
    padding: ({ variant }: TabButtonStyleProps) =>
      variant === 'big' ? '12px 20px' : `${SEGMENTED_PADDING_V}px ${SEGMENTED_PADDING_H}px`,
    font: fontText('normal', theme.fontSize.base, 24),
    border: 'none',
    color: ({ checked }: TabButtonStyleProps) =>
      checked ? theme.color.greys[0] : (theme.variables.isDarkTheme ? theme.color.greys[0] : theme.color.greys[80]),
    backgroundColor: ({ checked }: TabButtonStyleProps) =>
      checked
        ? (theme.variables.isDarkTheme ? theme.color.greys[82] : theme.color.greys[80])
        : 'transparent',
    borderRadius: SEGMENTED_RADIUS_INNER,
    [theme.breakpoints.down('xs')]: {
      padding: `${SEGMENTED_PADDING_V}px ${SEGMENTED_PADDING_H}px`,
    },

    '&:hover': {
      backgroundColor: ({ checked }: TabButtonStyleProps) =>
        checked
          ? (theme.variables.isDarkTheme ? theme.color.greys[50] : theme.color.greys[80])
          : (theme.variables.isDarkTheme ? theme.color.greys[75] : theme.color.greys[20]),
    },

    '&:active': {
      backgroundColor: ({ checked }: TabButtonStyleProps) =>
        checked
          ? (theme.variables.isDarkTheme ? theme.color.greys[70] : theme.color.greys[84])
          : (theme.variables.isDarkTheme ? theme.color.greys[75] : theme.color.greys[30]),
    },
  },
  multiButtonItem: {
    borderRadius: SEGMENTED_RADIUS_INNER,
    '&[data-disabled] $radioLabel': {
      backgroundColor: theme.color.greys[15],
      color: theme.color.functional.disabled,
      cursor: 'not-allowed',
    },
    '&[data-disabled] $radioLabel:hover, &[data-disabled] $radioLabel:active': {
      backgroundColor: ({ checked }: TabButtonStyleProps) =>
        checked
          ? (theme.variables.isDarkTheme ? theme.color.greys[60] : theme.color.greys[40])
          : (theme.variables.isDarkTheme ? theme.color.greys[75] : theme.color.greys[15]),
      color: theme.color.functional.disabled,
    },
    '@supports not (selector(:has(*)))': {
      '&:focus-within': {
        outline: 'none',
        boxShadow: `0 0 0 ${FOCUS_GAP}px ${theme.color.greys[0]}, 0 0 0 ${FOCUS_GAP + FOCUS_OUTLINE_WIDTH}px ${theme.color.greys[80]}`,
        zIndex: 1,
      },
    },
    '@supports (selector(:has(*)))': {
      '&:has(input:focus-visible)': {
        outline: 'none',
        boxShadow: `0 0 0 ${FOCUS_GAP}px ${theme.color.greys[0]}, 0 0 0 ${FOCUS_GAP + FOCUS_OUTLINE_WIDTH}px ${theme.color.greys[80]}`,
        zIndex: 1,
      },
    },
  },
}), { name: 'MultiButton', index: StylesIndexInjections.UI_ATOMS });
